/**
 * Opening hours as an Excel file (plan 5.5, docs/phase-5-plan.md): the Hours
 * tab downloads a template filled with the listing's current hours, the owner
 * edits it, and uploading it replaces all the hours (after a preview).
 *
 * One row = one HoursEntry. Cells accept Hebrew, English, or the code key, so
 * a file works whichever language it was downloaded in. Pure (no Firebase, no
 * i18n): labels come in through HoursXlsxLabels, and errors are codes the
 * component translates.
 */
import ExcelJS from 'exceljs';
import type { ClockSeason, DayOfWeek, HoursDayType, HoursEntry, RoundingConfig, TimeRule } from '../types';
import { MYZMANIM_FIELDS } from '../config/constants';
import { dayRuns, HOURS_SPECIAL_DAY_TYPES, sortHoursEntries } from './hoursForm';
import { SHABBOS_ENDS_ZMAN } from './openingHours';
import type { LabelOption } from './scheduleXlsx';

export const HOURS_SHEET = 'Hours';

/** Column order in the Hours sheet. */
export const HOURS_XLSX_COLUMNS = [
  'days',
  'specialDays',
  'open',
  'close',
  'closed',
  'season',
  'validFrom',
  'validTo',
  'specificDate',
  'noteHe',
  'noteEn',
] as const;
export type HoursXlsxColumn = (typeof HOURS_XLSX_COLUMNS)[number];

type Bilingual = { en: string; he: string };
type SeasonChoice = 'allYear' | ClockSeason;

export interface HoursXlsxLabels {
  /** Day names: full and short, per language. */
  days: { value: DayOfWeek; en: string; he: string; enShort: string; heShort: string }[];
  specialDays: LabelOption<HoursDayType>[];
  seasons: LabelOption<SeasonChoice>[];
  yes: Bilingual;
  no: Bilingual;
  /** Name of the "Shabbos ends" zman. */
  shabbosEnds: Bilingual;
  /** Rounding words, e.g. up → "למעלה". */
  rounding: LabelOption<'up' | 'down' | 'nearest'>[];
  headers: Record<HoursXlsxColumn, Bilingual>;
  /** Lines for the Instructions sheet. */
  instructions: { en: string[]; he: string[] };
  examplesTitle: Bilingual;
  /** Example rows for the Instructions sheet: [days, specialDays, open, close] per language. */
  examples: { en: string[][]; he: string[][] };
  listsTitles: { days: Bilingual; specialDays: Bilingual; zmanim: Bilingual };
}

export type HoursXlsxErrorCode =
  | 'noSheet'
  | 'tooMany'
  | 'unknownDay'
  | 'unknownSpecialDay'
  | 'missingDays'
  | 'dateAndDays'
  | 'missingTime'
  | 'badTime'
  | 'badClosed'
  | 'badSeason'
  | 'badDate'
  | 'seasonAndDates'
  | 'seasonWithDate'
  | 'datesOrder';

export interface HoursXlsxError {
  /** Excel row number (1 = header); 0 = the whole file. */
  rowNumber: number;
  code: HoursXlsxErrorCode;
  column?: HoursXlsxColumn;
  /** The cell text that was not understood. */
  value?: string;
}

export interface HoursXlsxResult {
  entries: HoursEntry[];
  errors: HoursXlsxError[];
}

// ---------- Small helpers ----------

/** For matching: lower case, no spaces, quotes, geresh or dashes. */
function norm(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/[\s'’"“”׳״\-_/.]/g, '');
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Excel gives a typed date or time as a Date (UTC), a typed time sometimes as a fraction of a day. */
function cellText(row: ExcelJS.Row, col: number): string {
  const v = row.getCell(col).value as unknown;
  if (v === null || v === undefined) return '';
  if (v instanceof Date) {
    if (v.getUTCFullYear() < 1901) return `${pad2(v.getUTCHours())}:${pad2(v.getUTCMinutes())}`;
    return `${v.getUTCFullYear()}-${pad2(v.getUTCMonth() + 1)}-${pad2(v.getUTCDate())}`;
  }
  if (typeof v === 'object') {
    const o = v as { text?: unknown; result?: unknown; richText?: { text: string }[] };
    if (o.richText) return o.richText.map((r) => r.text).join('').trim();
    if (o.text !== undefined) return String(o.text).trim();
    if (o.result !== undefined) return String(o.result).trim();
  }
  if (typeof v === 'number' && v >= 0 && v < 1) {
    const minutes = Math.round(v * 24 * 60) % (24 * 60);
    return `${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`;
  }
  return String(v).trim();
}

function splitList(raw: string): string[] {
  return raw
    .split(/[,،;]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// ---------- Days ----------

function dayLookup(labels: HoursXlsxLabels): Map<string, DayOfWeek> {
  const map = new Map<string, DayOfWeek>();
  const add = (label: string, day: DayOfWeek) => {
    const n = norm(label);
    if (n) map.set(n, day);
  };
  const english = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  labels.days.forEach((d) => {
    [d.en, d.he, d.enShort, d.heShort, english[d.value], english[d.value].slice(0, 3), String(d.value)].forEach((l) =>
      add(l, d.value)
    );
    add(`יום ${d.he}`, d.value);
  });
  // Hebrew letters (א … ו) and common Shabbos spellings.
  ['א', 'ב', 'ג', 'ד', 'ה', 'ו'].forEach((letter, i) => add(letter, i as DayOfWeek));
  ['shabbos', 'shabbat', 'sat', 'שבת', 'שבת קודש'].forEach((l) => add(l, 6));
  return map;
}

/** "Sun–Thu, Sat" / "ראשון-חמישי" / "0,1" → days. */
function parseDays(raw: string, lookup: Map<string, DayOfWeek>): { days: DayOfWeek[] } | { bad: string } {
  const days = new Set<DayOfWeek>();
  for (const part of splitList(raw)) {
    const range = part.split(/\s*[–—-]\s*/).filter(Boolean);
    if (range.length === 2 && lookup.has(norm(range[0])) && lookup.has(norm(range[1]))) {
      const from = lookup.get(norm(range[0]))!;
      const to = lookup.get(norm(range[1]))!;
      if (from > to) return { bad: part };
      for (let d = from; d <= to; d++) days.add(d as DayOfWeek);
      continue;
    }
    const day = lookup.get(norm(part));
    if (day === undefined) return { bad: part };
    days.add(day);
  }
  return { days: Array.from(days).sort((a, b) => a - b) };
}

/** Days as text, with ranges: [0,1,2,3,4] → "ראשון–חמישי". */
export function formatDays(days: DayOfWeek[], labels: HoursXlsxLabels, isHebrew: boolean): string {
  const name = (d: DayOfWeek) => {
    const l = labels.days.find((x) => x.value === d);
    return l ? (isHebrew ? l.he : l.en) : String(d);
  };
  return dayRuns(days)
    .map(([a, b]) => {
      if (a === b) return name(a);
      if (b === a + 1) return `${name(a)}, ${name(b)}`;
      return `${name(a)}–${name(b)}`;
    })
    .join(', ');
}

// ---------- Times ----------

const ROUND_TO = [5, 10, 15, 30];

function zmanLabels(labels: HoursXlsxLabels): { key: string; label: string }[] {
  const list: { key: string; label: string }[] = [];
  Object.entries(MYZMANIM_FIELDS).forEach(([key, he]) => {
    list.push({ key, label: key }, { key, label: he });
  });
  list.push(
    { key: SHABBOS_ENDS_ZMAN, label: SHABBOS_ENDS_ZMAN },
    { key: SHABBOS_ENDS_ZMAN, label: labels.shabbosEnds.en },
    { key: SHABBOS_ENDS_ZMAN, label: labels.shabbosEnds.he },
    ...['צאת השבת', 'צאת שבת', 'Shabbos ends', 'Motzei Shabbos'].map((label) => ({ key: SHABBOS_ENDS_ZMAN, label })),
    // Names in Excel files downloaded before November 4, 2026.
    ...["צאת השבת (שקיעה + 33 דק')", 'Shabbos ends (sunset + 33 min)'].map((label) => ({ key: SHABBOS_ENDS_ZMAN, label })),
    ...['candleLighting', 'Candle lighting', 'הדלקת נרות'].map((label) => ({ key: 'candleLighting', label }))
  );
  // Longest first, so an old "Shabbos ends (sunset + 33 min)" wins over the shorter name inside it.
  return list.sort((a, b) => b.label.length - a.label.length);
}

/** "20:00", "8:30", "Tzet +30", "צאת הכוכבים -15 למעלה 5". */
function parseTime(raw: string, labels: HoursXlsxLabels): TimeRule | null {
  const v = raw.trim();
  const fixed = /^(\d{1,2})[:.](\d{2})$/.exec(v);
  if (fixed) {
    const h = Number(fixed[1]);
    const m = Number(fixed[2]);
    return h < 24 && m < 60 ? { type: 'fixed', time: `${pad2(h)}:${pad2(m)}` } : null;
  }
  const lower = v.toLowerCase();
  const zman = zmanLabels(labels).find((z) => lower.startsWith(z.label.toLowerCase()));
  if (!zman) return null;
  let rest = v.slice(zman.label.length).trim();

  let offsetMinutes = 0;
  const offset = /^([+-])\s*(\d{1,3})/.exec(rest);
  if (offset) {
    offsetMinutes = Number(offset[2]) * (offset[1] === '-' ? -1 : 1);
    rest = rest.slice(offset[0].length).trim();
  }

  let rounding: RoundingConfig | undefined;
  if (rest) {
    const round = /^(.*?)\s*(\d{1,2})$/.exec(rest);
    if (!round) return null;
    const direction = labels.rounding.find((r) => [r.value, r.en, r.he].some((l) => norm(l) === norm(round[1])))?.value;
    const to = Number(round[2]);
    if (!direction || !ROUND_TO.includes(to)) return null;
    rounding = { to: to as RoundingConfig['to'], direction };
  }
  return { type: 'zmanOffset', baseZman: zman.key, offsetMinutes, ...(rounding ? { rounding } : {}) };
}

export function formatTime(rule: TimeRule | undefined, labels: HoursXlsxLabels, isHebrew: boolean): string {
  if (!rule) return '';
  if (rule.type === 'fixed') return rule.time;
  const name =
    rule.baseZman === SHABBOS_ENDS_ZMAN
      ? isHebrew
        ? labels.shabbosEnds.he
        : labels.shabbosEnds.en
      : isHebrew
        ? MYZMANIM_FIELDS[rule.baseZman] ?? rule.baseZman
        : rule.baseZman;
  const offset = rule.offsetMinutes ? ` ${rule.offsetMinutes > 0 ? '+' : '-'}${Math.abs(rule.offsetMinutes)}` : '';
  const r = rule.rounding;
  const dir = r ? labels.rounding.find((x) => x.value === r.direction) : undefined;
  const round = r ? ` ${dir ? (isHebrew ? dir.he : dir.en) : r.direction} ${r.to}` : '';
  return `${name}${offset}${round}`;
}

// ---------- Other cells ----------

function parseYesNo(raw: string, labels: HoursXlsxLabels): boolean | undefined | null {
  const n = norm(raw);
  if (!n) return undefined;
  if ([labels.yes.en, labels.yes.he, 'yes', 'y', 'true', '1', 'כן', 'closed', 'סגור'].some((l) => norm(l) === n)) return true;
  if ([labels.no.en, labels.no.he, 'no', 'n', 'false', '0', 'לא', 'open', 'פתוח'].some((l) => norm(l) === n)) return false;
  return null;
}

/** "2026-10-25", "25/10/2026", "25.10.2026" → "2026-10-25". */
function parseDate(raw: string): string | null {
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(raw);
  const local = /^(\d{1,2})[/.](\d{1,2})[/.](\d{4})$/.exec(raw);
  const [y, m, d] = iso ? [iso[1], iso[2], iso[3]] : local ? [local[3], local[2], local[1]] : [];
  if (!y) return null;
  const date = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
  if (date.getUTCMonth() !== Number(m) - 1 || date.getUTCDate() !== Number(d)) return null;
  return `${y}-${pad2(Number(m))}-${pad2(Number(d))}`;
}

// ---------- Parse ----------

/**
 * Reads the Hours sheet. Blank rows are skipped. `newId` makes the entry ids.
 * The caller replaces all the listing's hours with `entries` only when
 * `errors` is empty.
 */
export async function parseHoursXlsx(
  buffer: ArrayBuffer,
  labels: HoursXlsxLabels,
  newId: () => string,
  maxEntries: number
): Promise<HoursXlsxResult> {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buffer);
  const sheet = wb.getWorksheet(HOURS_SHEET);
  if (!sheet) return { entries: [], errors: [{ rowNumber: 0, code: 'noSheet' }] };

  const days = dayLookup(labels);
  const specialLookup = new Map<string, HoursDayType>();
  labels.specialDays.forEach((s) => [s.value, s.en, s.he].forEach((l) => specialLookup.set(norm(l), s.value)));
  const erevShabbos = new Set(['erev shabbos', 'erev shabbat', 'ערב שבת', 'erevShabbos'].map(norm));
  const seasonLookup = new Map<string, SeasonChoice>();
  labels.seasons.forEach((s) => [s.value, s.en, s.he].forEach((l) => seasonLookup.set(norm(l), s.value)));

  const col = (name: HoursXlsxColumn) => HOURS_XLSX_COLUMNS.indexOf(name) + 1;
  const entries: HoursEntry[] = [];
  const errors: HoursXlsxError[] = [];

  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber === 1) return;
    const cell = (name: HoursXlsxColumn) => cellText(row, col(name));
    if (HOURS_XLSX_COLUMNS.every((c) => !cell(c))) return;
    const fail = (code: HoursXlsxErrorCode, column?: HoursXlsxColumn) =>
      errors.push({ rowNumber, code, ...(column ? { column, value: cell(column) } : {}) });

    const parsedDays = parseDays(cell('days'), days);
    if ('bad' in parsedDays) return fail('unknownDay', 'days');

    const dayTypes: HoursDayType[] = [];
    for (const part of splitList(cell('specialDays'))) {
      const d = specialLookup.get(norm(part));
      // Erev Shabbos is Friday, and Shabbos a weekday, for opening hours (plan 5.2).
      const weekday = d ? undefined : erevShabbos.has(norm(part)) ? 5 : days.get(norm(part));
      if (weekday !== undefined) {
        if (!parsedDays.days.includes(weekday)) parsedDays.days = [...parsedDays.days, weekday].sort((a, b) => a - b);
        continue;
      }
      if (!d) return errors.push({ rowNumber, code: 'unknownSpecialDay', column: 'specialDays', value: part });
      if (!dayTypes.includes(d)) dayTypes.push(d);
    }
    const orderedTypes = HOURS_SPECIAL_DAY_TYPES.filter((d) => dayTypes.includes(d));

    const dates: Partial<Record<'validFrom' | 'validTo' | 'specificDate', string>> = {};
    for (const name of ['validFrom', 'validTo', 'specificDate'] as const) {
      if (!cell(name)) continue;
      const date = parseDate(cell(name));
      if (!date) return fail('badDate', name);
      dates[name] = date;
    }

    if (dates.specificDate && (parsedDays.days.length || orderedTypes.length)) return fail('dateAndDays');
    if (!dates.specificDate && !parsedDays.days.length && !orderedTypes.length) return fail('missingDays');

    let season: SeasonChoice | undefined;
    if (cell('season')) {
      season = seasonLookup.get(norm(cell('season')));
      if (!season) return fail('badSeason', 'season');
    }
    const hasRange = !!(dates.validFrom || dates.validTo);
    const clockSeason = season && season !== 'allYear' ? season : undefined;
    if (dates.specificDate && (clockSeason || hasRange)) return fail('seasonWithDate');
    if (clockSeason && hasRange) return fail('seasonAndDates');
    if (dates.validFrom && dates.validTo && dates.validFrom > dates.validTo) return fail('datesOrder');

    const closed = parseYesNo(cell('closed'), labels);
    if (closed === null) return fail('badClosed', 'closed');

    let open: TimeRule = { type: 'fixed', time: '00:00' };
    let close: TimeRule = { type: 'fixed', time: '00:00' };
    if (!closed) {
      for (const name of ['open', 'close'] as const) {
        if (!cell(name)) return fail('missingTime', name);
        const rule = parseTime(cell(name), labels);
        if (!rule) return fail('badTime', name);
        if (name === 'open') open = rule;
        else close = rule;
      }
    }

    const entry: HoursEntry = {
      id: newId(),
      daysOfWeek: dates.specificDate ? [] : parsedDays.days,
      dayTypes: dates.specificDate ? [] : orderedTypes,
      open,
      close,
    };
    if (closed) entry.closed = true;
    if (dates.specificDate) entry.specificDate = dates.specificDate;
    if (clockSeason) entry.season = clockSeason;
    if (dates.validFrom) entry.validFrom = dates.validFrom;
    if (dates.validTo) entry.validTo = dates.validTo;
    if (cell('noteHe')) entry.noteHe = cell('noteHe');
    if (cell('noteEn')) entry.noteEn = cell('noteEn');
    entries.push(entry);
  });

  if (entries.length > maxEntries) errors.push({ rowNumber: 0, code: 'tooMany' });
  return { entries, errors };
}

// ---------- Template ----------

/** One entry → the Hours sheet's cells, in the given language. */
export function hoursEntryToRow(entry: HoursEntry, labels: HoursXlsxLabels, isHebrew: boolean): Record<HoursXlsxColumn, string> {
  const pick = (b: Bilingual) => (isHebrew ? b.he : b.en);
  const special = (entry.dayTypes ?? [])
    .filter((d) => d !== 'weekday')
    .map((d) => {
      const l = labels.specialDays.find((s) => s.value === d);
      return l ? pick(l) : d;
    });
  const season = entry.season ? labels.seasons.find((s) => s.value === entry.season) : undefined;
  return {
    days: formatDays(entry.daysOfWeek ?? [], labels, isHebrew),
    specialDays: special.join(', '),
    open: entry.closed ? '' : formatTime(entry.open, labels, isHebrew),
    close: entry.closed ? '' : formatTime(entry.close, labels, isHebrew),
    closed: entry.closed ? pick(labels.yes) : '',
    season: season ? pick(season) : '',
    validFrom: entry.validFrom ?? '',
    validTo: entry.validTo ?? '',
    specificDate: entry.specificDate ?? '',
    noteHe: entry.noteHe ?? '',
    noteEn: entry.noteEn ?? '',
  };
}

const LAST_ROW = 200;

/** The template: the current hours, ready to edit, with an Instructions sheet. */
export function buildHoursWorkbook(hours: HoursEntry[], labels: HoursXlsxLabels, isHebrew: boolean): ExcelJS.Workbook {
  const pick = (b: Bilingual) => (isHebrew ? b.he : b.en);
  const wb = new ExcelJS.Workbook();

  const sheet = wb.addWorksheet(HOURS_SHEET, { views: [{ state: 'frozen', ySplit: 1, rightToLeft: isHebrew }] });
  const widths: Record<HoursXlsxColumn, number> = {
    days: 24, specialDays: 26, open: 22, close: 22, closed: 10, season: 18,
    validFrom: 13, validTo: 13, specificDate: 13, noteHe: 24, noteEn: 24,
  };
  sheet.columns = HOURS_XLSX_COLUMNS.map((c) => ({ header: pick(labels.headers[c]), key: c, width: widths[c] }));
  sheet.getRow(1).font = { bold: true };
  // Text cells, so Excel keeps "20:00" and "2026-10-25" as typed.
  HOURS_XLSX_COLUMNS.forEach((_, i) => {
    sheet.getColumn(i + 1).numFmt = '@';
  });
  sortHoursEntries(hours).forEach((h) => sheet.addRow(hoursEntryToRow(h, labels, isHebrew)));

  const lists = wb.addWorksheet('Lists');
  lists.state = 'veryHidden';
  [labels.yes, labels.no].forEach((b, i) => {
    lists.getCell(i + 1, 1).value = pick(b);
  });
  labels.seasons.forEach((s, i) => {
    lists.getCell(i + 1, 2).value = pick(s);
  });
  const dropdowns: [HoursXlsxColumn, string][] = [
    ['closed', 'Lists!$A$1:$A$2'],
    ['season', `Lists!$B$1:$B$${labels.seasons.length}`],
  ];
  for (let r = 2; r <= LAST_ROW; r++) {
    dropdowns.forEach(([c, ref]) => {
      sheet.getCell(r, HOURS_XLSX_COLUMNS.indexOf(c) + 1).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [ref],
      };
    });
  }

  const help = wb.addWorksheet(isHebrew ? 'הוראות' : 'Instructions', { views: [{ rightToLeft: isHebrew }] });
  help.columns = [{ width: 34 }, { width: 30 }, { width: 26 }, { width: 26 }];
  const lines = isHebrew ? labels.instructions.he : labels.instructions.en;
  lines.forEach((line, i) => {
    const row = help.addRow([line]);
    if (i === 0) row.font = { bold: true, size: 13 };
    row.alignment = { wrapText: false };
  });
  help.addRow([]);
  help.addRow([pick(labels.examplesTitle)]).font = { bold: true };
  help.addRow([pick(labels.headers.days), pick(labels.headers.specialDays), pick(labels.headers.open), pick(labels.headers.close)]).font = { italic: true };
  (isHebrew ? labels.examples.he : labels.examples.en).forEach((ex) => help.addRow(ex));
  help.addRow([]);
  help.addRow([pick(labels.listsTitles.days)]).font = { bold: true };
  help.addRow([labels.days.map(pick).join(', ')]);
  help.addRow([]);
  help.addRow([pick(labels.listsTitles.specialDays)]).font = { bold: true };
  labels.specialDays.forEach((s) => help.addRow([pick(s)]));
  help.addRow([]);
  help.addRow([pick(labels.listsTitles.zmanim)]).font = { bold: true };
  Object.entries(MYZMANIM_FIELDS).forEach(([key, he]) => help.addRow([isHebrew ? he : key]));
  help.addRow([pick(labels.shabbosEnds)]);
  return wb;
}

/** The template as a file to download. */
export async function generateHoursXlsx(hours: HoursEntry[], labels: HoursXlsxLabels, isHebrew: boolean): Promise<Blob> {
  const buffer = await buildHoursWorkbook(hours, labels, isHebrew).xlsx.writeBuffer();
  return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

// ---------- Labels from the translations ----------

type TFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * Builds the labels from an English and a Hebrew translate function (e.g.
 * i18n.getFixedT('en') / getFixedT('he')), so the file reads both languages.
 */
export function buildHoursXlsxLabels(tEn: TFn, tHe: TFn): HoursXlsxLabels {
  const both = (key: string, options?: Record<string, unknown>): Bilingual => ({ en: tEn(key, options), he: tHe(key, options) });
  const up = 'directory.admin.hoursUpload';
  const days = ([0, 1, 2, 3, 4, 5, 6] as DayOfWeek[]).map((d) => ({
    value: d,
    en: tEn(`directory.dayOfWeek.${d}`),
    he: tHe(`directory.dayOfWeek.${d}`),
    enShort: tEn(`directory.dayOfWeekShort.${d}`),
    heShort: tHe(`directory.dayOfWeekShort.${d}`),
  }));
  const label = (d: HoursDayType) => ({ value: d, ...both(`dayTypes.${d}`) });
  const shabbosEnds = both('directory.admin.hours.shabbosEnds');
  const pickDays = (isHebrew: boolean, list: DayOfWeek[]) =>
    formatDays(list, { days } as HoursXlsxLabels, isHebrew);
  const example = (isHebrew: boolean): string[][] => {
    const p = (b: Bilingual) => (isHebrew ? b.he : b.en);
    return [
      [pickDays(isHebrew, [0, 1, 2, 3, 4]), '', '20:00', '22:00'],
      [pickDays(isHebrew, [5]), p(both('dayTypes.erevYomTov')), '10:00', `${isHebrew ? MYZMANIM_FIELDS.SunsetDefault : 'SunsetDefault'} -60`],
      [pickDays(isHebrew, [6]), '', `${p(shabbosEnds)} +30`, '23:00'],
      ['', p(both('dayTypes.yomKippur')), '', '', p(both(`${up}.yes`))],
    ];
  };
  return {
    days,
    specialDays: HOURS_SPECIAL_DAY_TYPES.map(label),
    seasons: (['allYear', 'winterClock', 'summerClock'] as SeasonChoice[]).map((s) => ({
      value: s,
      ...both(`directory.admin.hours.season.${s}`),
    })),
    yes: both(`${up}.yes`),
    no: both(`${up}.no`),
    shabbosEnds,
    rounding: (['up', 'down', 'nearest'] as const).map((d) => ({
      value: d,
      ...both(`admin.round${d.charAt(0).toUpperCase()}${d.slice(1)}`),
    })),
    headers: Object.fromEntries(HOURS_XLSX_COLUMNS.map((c) => [c, both(`${up}.column.${c}`)])) as Record<
      HoursXlsxColumn,
      Bilingual
    >,
    instructions: {
      en: [1, 2, 3, 4, 5, 6, 7].map((i) => tEn(`${up}.instructions.line${i}`)),
      he: [1, 2, 3, 4, 5, 6, 7].map((i) => tHe(`${up}.instructions.line${i}`)),
    },
    examplesTitle: both(`${up}.instructions.examples`),
    examples: { en: example(false), he: example(true) },
    listsTitles: {
      days: both(`${up}.instructions.days`),
      specialDays: both(`${up}.instructions.specialDays`),
      zmanim: both(`${up}.instructions.zmanim`),
    },
  };
}
